import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchPayOUTData } from '../../services/dataApi';
import PageLoader from '../../components/PageLoader';
import OrdersTable from '../../components/OrdersTable';
import Tabs from '../../components/Tabs';
import OrderTools, { toRangeBoundary } from '../../components/OrderTools';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import useUrlOrderFilters from '../../hooks/useUrlOrderFilters';
import useLinkedOrder from '../../hooks/useLinkedOrder';
import useAutomaticRetry from '../../hooks/useAutomaticRetry';
import '../Page.css';

const AdminPayOUT = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const {
    activeTab, currentPage, itemsPerPage, searchTerm, orderFilters,
    dateFrom, dateTo, dateFromTime, dateToTime, dateTimezone,
    setCurrentPage, setActiveTab, setSearchTerm, applyFilters
  } = useUrlOrderFilters();
  const [pagination, setPagination] = useState(null);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400);
  const linkedOrder = useLinkedOrder(fetchPayOUTData);
  const latestRequestId = useRef(0);

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'pending', label: 'Pending' },
    { id: 'completed', label: 'Completed' },
    { id: 'cancelled', label: 'Cancelled' }
  ];

  const loadData = useCallback(async (
    status = activeTab, page = 1, term = debouncedSearchTerm, { silent = false } = {}
  ) => {
    const requestId = ++latestRequestId.current;
    if (!silent) setLoading(true);
    setError(null);
    
    try {
      const result = await fetchPayOUTData(
        status, page, itemsPerPage, term,
        toRangeBoundary(dateFrom, false, dateTimezone, dateFromTime),
        toRangeBoundary(dateTo, true, dateTimezone, dateToTime),
        orderFilters
      );
      if (requestId !== latestRequestId.current) return;
      setData(result.data || result);
      setPagination(result.pagination);
    } catch (err) {
      if (requestId !== latestRequestId.current) return;
      if (silent) {
        console.error('Failed to refresh admin PayOUT data:', err);
      } else {
        setError(err.message || 'An error occurred while loading data');
      }
    } finally {
      if (requestId === latestRequestId.current) setLoading(false);
    }
  }, [activeTab, itemsPerPage, debouncedSearchTerm, dateFrom, dateTo, dateFromTime, dateToTime, dateTimezone, orderFilters]);

  useEffect(() => {
    loadData(activeTab, currentPage, debouncedSearchTerm);
    return () => {
      latestRequestId.current += 1;
    };
  }, [activeTab, currentPage, debouncedSearchTerm, loadData]);

  const refreshCurrentViewSilently = useCallback(() => {
    loadData(activeTab, currentPage, debouncedSearchTerm, { silent: true });
  }, [activeTab, currentPage, debouncedSearchTerm, loadData]);

  const retryCurrentView = useCallback(() => {
    loadData(activeTab, currentPage, debouncedSearchTerm);
  }, [activeTab, currentPage, debouncedSearchTerm, loadData]);
  useAutomaticRetry(Boolean(error && !data), retryCurrentView);

  const handleLiveTotalChange = useCallback((delta) => {
    setPagination((current) => {
      if (!current) return current;
      const total = Math.max(0, Number(current.total || 0) + delta);
      return {
        ...current,
        total,
        totalPages: Math.max(1, Math.ceil(total / current.limit))
      };
    });
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const visibleCount = Array.isArray(data) ? data.length : (data?.data?.length || 0);
  const totalCount = pagination?.total ?? visibleCount;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading">
          <h1>PayOUT</h1>
        </div>
        <div className="page-overview">
          <div className="overview-card">
            <div className="overview-label">Current View</div>
            <div className="overview-value">{activeTab}</div>
            <div className="overview-meta">Admin-wide filter</div>
          </div>
          <div className="overview-card">
            <div className="overview-label">Total Orders</div>
            <div className="overview-value">{totalCount}</div>
            <div className="overview-meta">Across all traders</div>
          </div>
            <div className="overview-card">
              <div className="overview-label">Visible Orders</div>
              <div className="overview-value">{visibleCount}</div>
              <div className="overview-meta">{searchTerm ? 'Current search page' : 'Current page snapshot'}</div>
            </div>
        </div>
      </div>
      <div className="page-content">
        <PageLoader loading={loading && !data} error={error}>
          {data ? (
            <div className="page-data">
              <div className="page-section-header">
                <div className="page-section-title">
                  <h2>PayOUT</h2>
                </div>
                <div className="page-section-meta">
                  <div className="page-chip">Page {pagination?.page || 1}</div>
                  <div className="page-chip">{itemsPerPage} orders per page</div>
                </div>
              </div>
              <div className="orders-context-bar">
              <Tabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
              <OrderTools
                dateFrom={dateFrom}
                dateTo={dateTo}
                dateFromTime={dateFromTime}
                dateToTime={dateToTime}
                dateTimezone={dateTimezone}
                onApplyFilters={applyFilters}
                fetchOrders={fetchPayOUTData}
                pageType="payout"
                filters={orderFilters}
                statusFilter={activeTab}
                onStatusChange={handleTabChange}
                searchTerm={searchTerm}
                onSearch={handleSearch}
                searchPlaceholder="Search by trader, gateway, order id, request id, method..."
              />
              </div>
              <OrdersTable 
                data={data}
                pageType="payout"
                statusFilter={activeTab}
                searchTerm={debouncedSearchTerm}
                filters={orderFilters}
                dateFrom={toRangeBoundary(dateFrom, false, dateTimezone, dateFromTime)}
                dateTo={toRangeBoundary(dateTo, true, dateTimezone, dateToTime)}
                showTraderName={true}
                pagination={pagination}
                onPageChange={handlePageChange}
                onRefreshNeeded={refreshCurrentViewSilently}
                onTotalChange={handleLiveTotalChange}
                linkedOrder={linkedOrder}
              />
            </div>
          ) : (
            <div className="page-placeholder">
              <p>No data received</p>
            </div>
          )}
        </PageLoader>
      </div>
    </div>
  );
};

export default AdminPayOUT;
